/**
 * AIDisclosureNotice — Disclosure UX do Companion (HLOS-225)
 *
 * Componente Vue 3 (Composition API, SFC com bloco de script setup TypeScript)
 * que explica ao usuário, em linguagem neutra (Princípio XI: zero menção a
 * provider LLM), que:
 *   - O Companion usa "Inteligência do iPhone" para responder localmente.
 *   - Tudo acontece no dispositivo; nada sai do cofre local.
 *   - A Inteligência do iPhone é desativável a qualquer momento.
 *
 * 2 escopos:
 *   - "onboarding" — versão completa, mostrada após mnemonic no 5o turno
 *     (gate: disclosureDismissed antes de habilitar "Concluir").
 *   - "settings"  — versão collapsed, link "Saiba mais" expande para a versão
 *     completa.
 *
 * Constitucional:
 *   - Princípio XI (Companion ≠ LLM): copy NUNCA cita "Foundation Models",
 *     "Apple Intelligence", "GPT", "WebLLM", "MLX", etc. Gate programático
 *     em runtime (this file) + em testes (AIDisclosureNotice.test.ts).
 *   - Princípio X (Dignidade): zero "tem certeza que NÃO quer desativar?",
 *     zero Streak, zero FOMO. Botão principal é "Entendi" (não "Pular").
 *   - CL-07 (vault local-first): disclosure explica "tudo local".
 *   - CL-08 (Ética fechada): disclosure inclui instruções para desativar.
 *   - CC-06 (copy respeitosa): label do botão é action-oriented ("Entendi",
 *     "Saiba mais"), nunca "Pular" ou "Ignorar".
 *
 * Skills HLOS governando:
 *   - vue-shell: Composition API, scoped CSS, data-testid para Playwright.
 *   - companion-product: 3 estados canônicos, R1-R6, Privacy Footer sempre.
 *   - constitutional-lock: gate CL/CC/Princípios em CI.
 */

<script setup lang="ts">
import { computed } from "vue";

/** Escopo de uso do componente. */
type Scope = "onboarding" | "settings";

interface Props {
  /** Escopo. Default: onboarding (versão completa). */
  scope?: Scope;
  /** Versão colapsada (apenas título + botão "Saiba mais") para Settings. */
  collapsed?: boolean;
  /** Se true, esconde botão "Entendi" (usado para Settings onde disclosure
   *  é exibida sempre, sem dismiss). Default: false. */
  dismissible?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  scope: "onboarding",
  collapsed: false,
  dismissible: true,
});

const emit = defineEmits<{
  /** Emitido quando usuário clica em "Entendi". Pai controla state. */
  dismiss: [];
}>();

function onDismiss(): void {
  if (props.dismissible) emit("dismiss");
}

/**
 * Copy PT-BR (hardcoded em v0.2 — refatorar para i18n em Sprint 10).
 *
 * CONSTITUCIONAL: zero menção a provider LLM (Princípio XI). Strings revisadas
 * para usar apenas termos neutros ("Inteligência do iPhone", "Companion",
 * "tudo local"). Gate programático abaixo (sanityCheck).
 */
const COPY = {
  title: "Como o Companheiro responde",
  main: "No seu iPhone, o Companheiro usa a Inteligência do iPhone para responder — ela roda inteiramente no seu dispositivo, sem enviar dados para a Apple nem para a HLOS.",
  bullets: [
    "Tudo acontece no seu iPhone — nada sai do dispositivo",
    "Memória, metas e identidade ficam cifradas no cofre local",
    "A Inteligência do iPhone é desativável a qualquer momento",
  ] as const,
  howToDisable: "Configurações → Apps → Companheiro → Inteligência do iPhone → desativar.",
  whatChanges: "O Companheiro continua funcionando — apenas responde mais devagar porque usa uma alternativa. Sua memória privada continua igual.",
  closingNote: 'Você pode revisar essa explicação em Configurações → Privacidade → "Como o Companheiro responde".',
  ctaDismiss: "Entendi",
  ctaExpand: "Saiba mais",
  ctaCollapse: "Mostrar menos",
} as const;

/**
 * Gate programático — Princípio XI.
 *
 * Se QUALQUER forbidden literal aparecer em COPY (incluindo em strings
 * hardcoded ou valores calculados), lança erro de desenvolvimento no
 * momento de mount do componente. Isso garante que copy nunca cite
 * provider LLM nem implícita nem explicitamente.
 *
 * Lista de forbidden literals é conservadora: cobre nomes comuns de
 * framework (qnn=web, mlx, etc.) e provedores externos (openai, etc.).
 */
const FORBIDDEN_LITERALS = [
  // Provedores externos (Princípio XI — nunca expor)
  "openai",
  "anthropic",
  "chatgpt",
  "claude",
  "gemini",
  "huggingface",
  "cohere",
  // Frameworks/termos técnicos (Companion ≠ LLM)
  "foundation",
  "webllm",
  "mlx",
  "qnn",
  "onnx",
  // Nome próprio da Apple Intelligence (substituímos por "Inteligência do iPhone")
  "apple intelligence",
  "apple-intelligence",
] as const;

const copyLower: string = JSON.stringify(COPY).toLowerCase();

/** Erro lançado se copy contiver literal forbidden (gate Princípio XI). */
const FORBIDDEN_HIT: readonly string[] = FORBIDDEN_LITERALS.filter((needle) =>
  copyLower.includes(needle),
);

if (FORBIDDEN_HIT.length > 0) {
  // Erro de DESENVOLVIMENTO, não user-facing. Lançado uma vez por mount.
  throw new Error(
    "[AIDisclosureNotice] Princípio XI VIOLADO — copy contém literal forbidden: " +
      FORBIDDEN_HIT.join(", "),
  );
}

const isOnboarding = computed<boolean>(() => props.scope === "onboarding");
const isSettingsCollapsed = computed<boolean>(
  () => props.scope === "settings" && props.collapsed,
);
</script>

<template>
  <aside
    class="ai-disclosure"
    :class="{ 'ai-disclosure--collapsed': isSettingsCollapsed }"
    role="region"
    :aria-label="COPY.title"
    data-testid="ai-disclosure"
    :data-scope="scope"
  >
    <header class="ai-disclosure__header">
      <h3 class="ai-disclosure__title">{{ COPY.title }}</h3>
      <button
        v-if="isOnboarding && dismissible && !collapsed"
        type="button"
        class="ai-disclosure__dismiss"
        data-testid="ai-disclosure-dismiss"
        @click="onDismiss"
      >
        {{ COPY.ctaDismiss }}
      </button>
      <button
        v-if="scope === 'settings' && collapsed"
        type="button"
        class="ai-disclosure__expand"
        data-testid="ai-disclosure-expand"
        @click="$emit('dismiss') /* expand: pai controla state */"
        :aria-expanded="false"
      >
        {{ COPY.ctaExpand }}
      </button>
    </header>

    <section v-if="!collapsed" class="ai-disclosure__body" data-testid="ai-disclosure-body">
      <p class="ai-disclosure__main">{{ COPY.main }}</p>

      <ul class="ai-disclosure__bullets" data-testid="ai-disclosure-bullets">
        <li
          v-for="bullet in COPY.bullets"
          :key="bullet"
          class="ai-disclosure__bullet"
          data-testid="ai-disclosure-bullet"
        >
          {{ bullet }}
        </li>
      </ul>

      <details class="ai-disclosure__details" data-testid="ai-disclosure-howto-disable">
        <summary class="ai-disclosure__summary">Como desativar</summary>
        <p>{{ COPY.howToDisable }}</p>
      </details>

      <details class="ai-disclosure__details" data-testid="ai-disclosure-what-changes">
        <summary class="ai-disclosure__summary">O que muda se eu desativar</summary>
        <p>{{ COPY.whatChanges }}</p>
      </details>
    </section>

    <p
      v-if="isOnboarding && !collapsed && dismissible"
      class="ai-disclosure__note"
      data-testid="ai-disclosure-note"
    >
      {{ COPY.closingNote }}
    </p>
  </aside>
</template>

<style scoped>
.ai-disclosure {
  max-width: 720px;
  margin: 1.5rem auto;
  padding: 1.25rem 1.5rem;
  border: 1px solid color-mix(in srgb, currentColor 20%, transparent);
  border-radius: 0.75rem;
  background: color-mix(in srgb, currentColor 4%, transparent);
}
.ai-disclosure--collapsed {
  /* Settings collapsed: minimal padding, summary-style */
  padding: 0.75rem 1rem;
  margin: 0.5rem auto;
}
.ai-disclosure__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
}
.ai-disclosure__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.3;
}
.ai-disclosure__dismiss,
.ai-disclosure__expand {
  font: inherit;
  padding: 0.4rem 0.9rem;
  border: 1px solid currentColor;
  border-radius: 0.375rem;
  background: transparent;
  color: inherit;
  cursor: pointer;
  /* CC-06 / Princípio X: zero dark pattern — botão neutro, sem cor de "alerta" */
}
.ai-disclosure__dismiss:hover:not(:disabled),
.ai-disclosure__expand:hover:not(:disabled) {
  background: color-mix(in srgb, currentColor 10%, transparent);
}
.ai-disclosure__body {
  margin-top: 0.5rem;
}
.ai-disclosure__main {
  margin: 0 0 0.75rem 0;
  line-height: 1.5;
}
.ai-disclosure__bullets {
  margin: 0 0 1rem 1.25rem;
  padding: 0;
}
.ai-disclosure__bullet {
  margin-bottom: 0.4rem;
  line-height: 1.4;
}
.ai-disclosure__details {
  margin-bottom: 0.5rem;
  border-top: 1px solid color-mix(in srgb, currentColor 15%, transparent);
  padding-top: 0.5rem;
}
.ai-disclosure__summary {
  cursor: pointer;
  font-weight: 500;
  font-size: 0.95em;
  /* CC-01..08: zero hover trap, sem "?" ou warning */
  list-style: none;
}
.ai-disclosure__summary::-webkit-details-marker {
  /* Remove default triangle on webkit */
  display: none;
}
.ai-disclosure__summary::before {
  content: "+ ";
  font-weight: 700;
  opacity: 0.6;
}
details[open] .ai-disclosure__summary::before {
  content: "− ";
}
.ai-disclosure__note {
  margin: 1rem 0 0 0;
  font-size: 0.85em;
  opacity: 0.75;
}
</style>
